import type { AxiosInstance } from "axios";

import { toast } from "sonner";

import type { Contract } from "@/types/contract";
import type { T_PaginationParams } from "@/types/filters";

import { keepPreviousData, useMutation, useQuery } from "@tanstack/react-query";

import { usePrivateAxios } from "./use-private-axios";

interface ContractsQueryResponse {
	data: Contract[];

	totalItems: number;
	totalPages: number;
	currentPage: number;
	itemsPerPage: number;

}

interface ContractQueryResponse {
	data: Contract;
}

async function fetchContracts<T extends T_PaginationParams>(axios: AxiosInstance, fetchParams?: T) {
	const params = {
		...fetchParams,
		page: (fetchParams?.pageIndex ?? 0) + 1,
		limit: fetchParams?.pageSize ?? 10
	};
	delete params.pageSize;
	delete params.pageIndex;
	const { data } = await axios.get<ContractsQueryResponse>("/contract/view_contracts", {
		params
	});

	return data;
}

async function fetchContract(axios: AxiosInstance, contractId: string) {
	const data = await axios.get<ContractQueryResponse>(`/contract/view_contract/${contractId}`);
	return data.data.data;
}
async function fetchContractFromCode(axios: AxiosInstance, contractCode: string | number) {
	const data = await axios.get<ContractQueryResponse>(`/contract/view_contract_by_code/${contractCode}`);
	return data.data.data;
}

export function useContracts<T extends T_PaginationParams>(params?: T) {
	const privateAxios = usePrivateAxios();
	return useQuery({
		queryKey: ["contracts"],
		queryFn: () => fetchContracts(privateAxios, params),
		placeholderData: keepPreviousData
	});
}
export function useCreateContract() {
	const privateAxios = usePrivateAxios();
	return useMutation({
		mutationKey: ["contracts"],
		mutationFn: () => fetchContracts(privateAxios)
	});
}
export function useContract(key: { contractId?: string; contractCode?: string | number }) {
	const privateAxios = usePrivateAxios();
	return useQuery({
		enabled: !!key?.contractId || !!key?.contractCode,
		queryKey: ["contract", key?.contractId, key?.contractCode],
		queryFn: () => {
			if (key?.contractCode)
				return fetchContractFromCode(privateAxios, key?.contractCode);
			if (key?.contractId)
				return fetchContract(privateAxios, key?.contractId);
			throw new Error("Contract ID or Code is required");
		}

	});
}
export function useCancelContract() {
	const privateAxios = usePrivateAxios();
	return useMutation({
		mutationKey: ["cancelContract"],
		mutationFn: async (contractId: string) => {
			const data = await privateAxios.patch(`/contract/${contractId}/cancel`, {
				id: contractId
			});
			return data.data;
		},
		onSuccess: () => {
			toast.success("Contract cancelled successfully");
		},
		onError: (error) => {
			console.error(error);
			if ((error as any).response?.data.message.includes("Only contracts with status")) {
				toast.error("Only contracts with status Pending can be canceled");
			}
			else {
				toast.error("An error occurred while cancelling contract");
			}
		}
	});
}

export interface Meta {
	totalItems: number;
	itemCount: number;
	itemsPerPage: number;
	totalPages: number;
	currentPage: number;
}

interface TiedContractsQueryResponse {
	data: {
		items: Contract[];
		meta: Meta;
	};
}

export function useViewTiedContracts() {
	const privateAxios = usePrivateAxios();
	return useQuery({
		queryKey: ["tiedContracts"],
		queryFn: async () => {
			const { data } = await privateAxios.get<TiedContractsQueryResponse>(`/contract/view_tie_contracts`);
			return data.data;
		}
	});
}
